import React from "react";
import { useGetMyCompanyRubricQuery } from "../../services/api";
import "./Competencies.css";

/**
 * CompetencyRubric Page (User-facing)
 * Displays the published competency rubric for the user's company
 * Read-only view showing competencies rated by role
 * Route: /competencies
 */
export default function CompetencyRubric() {
  // Fetch the published rubric for the user's company
  const { data: rubric, isLoading, isError, error } = useGetMyCompanyRubricQuery();

  // Loading state
  if (isLoading) {
    return (
      <div className="competencies-container">
        <button className="welcome">← Back Home</button>
        <h1 className="username">Competency Rubrics</h1>
        <div className="info-box">Loading rubric...</div>
      </div>
    );
  }

  // Error or no rubric state
  if (isError || !rubric) {
    return (
      <div className="competencies-container">
        <button className="welcome">← Back Home</button>
        <h1 className="username">Competency Rubrics</h1>
        <div className="info-box">
          <span role="img" aria-label="info">ℹ️</span>
          {isError
            ? error?.data?.message || "Unable to load rubric"
            : "No competency rubric has been published for your company yet."}
        </div>
      </div>
    );
  }

  // Group rows by value for alternating background
  let lastValueIndex = -1;
  let valueGroupIndex = 0;

  // Get rating pill class
  const getPillClass = (rating) => {
    if (!rating) return "";
    if (rating === "Ideal") return "competency-pill-ideal";
    if (rating === "Acceptable") return "competency-pill-acceptable";
    return "competency-pill-custom";
  };

  return (
    <div className="competencies-container">
      {/* Breadcrumb */}
      <button className="welcome">← Back Home</button>

      {/* Title + subtitle + action */}
      <div className="header-row">
        <div>
          <h1 className="username">Competency Rubrics</h1>
          <p className="competencies-subtitle">
            These rubrics show what "good" looks like at different job levels
            and performance bands. They transform abstract values into concrete,
            observable behaviors.
          </p>
        </div>
        <button className="download-btn">Download Pdf →</button>
      </div>

      {/* Info/alert box */}
      <div className="info-box">
        <span role="img" aria-label="info">
          💡
        </span>
        {rubric.competencySetName
          ? `Competency Set: ${rubric.competencySetName}`
          : "This rubric shows competency expectations for different roles in your organization."}
      </div>

      {/* Rubric */}
      <div className="rubric-section">
        <h2 className="rubric-heading">{rubric.competencySetName || "Competency Rubric"}</h2>
        <p className="rubric-desc">
          Rate expectations by role for each competency within your organization's values.
        </p>

        <div className="ct-table-wrap">
          <table
            className="ct-table"
            role="table"
            aria-label="Competency Rubric"
          >
            <thead>
              <tr>
                <th className="ct-col-values">Values</th>
                <th className="ct-col-competency">Competency</th>
                {rubric.roles?.map((role, index) => (
                  <th key={index}>{role.name}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rubric.tableData?.map((row, rowIndex) => {
                // Track value groups for alternating backgrounds
                if (row.valueIndex !== lastValueIndex) {
                  lastValueIndex = row.valueIndex;
                  valueGroupIndex++;
                }
                const isAltGroup = valueGroupIndex % 2 === 0;

                return (
                  <tr
                    key={`${row.valueIndex}-${row.competencyIndex}`}
                    className={isAltGroup ? "ct-group-alt" : ""}
                  >
                    {/* Values column with rowSpan */}
                    {row.isFirstInValue && (
                      <td className="ct-values" rowSpan={row.valueRowSpan}>
                        {row.valueName}
                      </td>
                    )}

                    {/* Competency column */}
                    <td className="ct-competency">{row.competencyName}</td>

                    {/* Rating cells for each role */}
                    {row.ratings?.map((ratingData, rIndex) => (
                      <td key={rIndex}>
                        {ratingData.rating ? (
                          <span className={`competency-pill ${getPillClass(ratingData.rating)}`}>
                            {ratingData.rating}
                          </span>
                        ) : (
                          <span className="ct-na"></span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
